import { useEffect, useRef } from "react";
import { CalendarDaysIcon } from "@heroicons/react/24/outline";
import { DayPicker } from "react-day-picker";
import { DAY_PICKER_CLASS_NAMES, DEFAULT_DATE } from "../constants";

export default function DashboardFilters({
  rangeLabel,
  rangeFrom,
  selectedRange,
  visibleMonth,
  isCalendarOpen,
  status,
  search,
  isMultiDayRange,
  onToggleCalendar,
  onMonthChange,
  onSelectRange,
  onCloseCalendar,
  onClearFilters,
  onStatusChange,
  onSearchChange,
}) {
  const calendarRef = useRef(null);

  useEffect(() => {
    if (!isCalendarOpen) return undefined;

    const handlePointerDown = (event) => {
      if (!calendarRef.current?.contains(event.target)) {
        onCloseCalendar();
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onCloseCalendar();
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isCalendarOpen, onCloseCalendar]);

  return (
    <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span className="font-semibold text-gray-700">Filter:</span>
          <div className="relative" ref={calendarRef}>
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-lg border border-cyan-300 bg-cyan-500 px-3 py-2 text-sm font-medium text-white"
              onClick={() => onToggleCalendar(rangeFrom || DEFAULT_DATE)}
              aria-expanded={isCalendarOpen}
              aria-haspopup="dialog"
            >
              <CalendarDaysIcon className="h-4 w-4" />
              {rangeLabel}
            </button>
            {isCalendarOpen ? (
              <div className="absolute left-0 top-12 z-20 rounded-xl border border-gray-200 bg-white p-3 shadow-xl">
                <DayPicker
                  mode="range"
                  selected={selectedRange}
                  month={visibleMonth}
                  onMonthChange={onMonthChange}
                  onSelect={onSelectRange}
                  showOutsideDays
                  className="text-sm"
                  classNames={DAY_PICKER_CLASS_NAMES}
                />
                <div className="mt-2 flex items-center justify-end gap-2 border-t border-gray-100 pt-2">
                  <button
                    type="button"
                    className="rounded-md px-2 py-1 text-xs font-semibold text-gray-600 hover:bg-gray-100"
                    onClick={onCloseCalendar}
                  >
                    Done
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
        <button
          type="button"
          className="text-sm font-semibold text-gray-600 hover:text-gray-800"
          onClick={onClearFilters}
        >
          Clear
        </button>
        <label className="sr-only" htmlFor="epaper-status-filter">
          Status
        </label>
        <select
          id="epaper-status-filter"
          className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700"
          value={status}
          onChange={(event) => onStatusChange(event.target.value)}
        >
          <option value="All Status">All Status</option>
          <option value="Success">Successful</option>
          <option value="Failed">Failed</option>
          <option value="Pending">Pending</option>
        </select>
        <label className="sr-only" htmlFor="epaper-search">
          Search edition
        </label>
        <input
          id="epaper-search"
          type="search"
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder="Search edition..."
          className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700"
        />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {isMultiDayRange ? (
          <span className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800">
            Updates are disabled for a date range. Select a single day to update editions.
          </span>
        ) : null}
      </div>
    </div>
  );
}
